import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useProperty, usePropertyField, usePropertyAddressUrl, useCollectionBinding } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { pages } from '../pages';

interface NavChild {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href?: string;
  children?: NavChild[];
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', href: '/' },
  {
    // Grouping label only — no 'Find Your Home' page exists. Communities
    // without an interactive map collapse this to a plain Floor Plans link.
    label: 'Find Your Home',
    children: [
      { label: 'Floor Plans', href: '/floor-plans' },
      { label: 'Interactive Map', href: '/interactive-map' },
    ],
  },
  {
    label: 'Features',
    children: [
      { label: 'Amenities', href: '/amenities' },
      { label: 'Pet Policy', href: '/pet-policy' },
      { label: 'Future Residents Club', href: '/future-residents-club' },
    ],
  },
  {
    label: 'Gallery & Tours',
    children: [
      { label: 'Gallery', href: '/gallery' },
      { label: 'Virtual Tours', href: '/virtual-tour' },
    ],
  },
  { label: 'Neighborhood', href: '/neighborhood' },
  { label: 'Contact Us', href: '/contact-us' },
];

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Which communities have an interactive map. Engrain SightMap is the platform's
 * interactive-map integration (see the `engrain-sitemap` block), so the community's
 * SightMap custom field is the signal — the same mechanism the Pay Rent link uses
 * with `custom_pay_rent_url`. The map blurb can't serve here: the CMS writes one for
 * nearly every community, with or without a map.
 */
const INTERACTIVE_MAP_FIELDS = [
  'custom_engrain_sightmap_id',
  'custom_engrain_sightmap_url',
  'custom_engrain_sitemap_id',
  'custom_engrain_sitemap_url',
];

function useHasInteractiveMap(): boolean {
  const property = useProperty();

  // A page hidden in pages.json isn't routed, so it must not be linked either
  if (pages['interactive-map']?.visible === false) return false;

  return INTERACTIVE_MAP_FIELDS.some((field) => {
    const value = property[field];
    return typeof value === 'string' && value.trim() !== '';
  });
}

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/apply-now',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/resident-access',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const payRentLinkDef: SlotGroupDefinition = {
  label: 'Pay Rent Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'custom_pay_rent_url',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Pay Rent' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Returns a digits-only (with optional leading +) string for use in tel: links,
 *  or null when the value is empty/missing. */
function cleanPhoneForTel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[^+\d]/g, '');
  return cleaned || null;
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function NavDropdown({
  item,
  isOpen,
  onOpen,
  onClose,
}: {
  item: NavItem;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  if (!item.children) {
    return (
      <a
        href={item.href}
        className="font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] hover:opacity-80 underline-offset-4 hover:underline transition-all py-2"
      >
        {item.label}
      </a>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
    >
      <button
        onClick={() => (isOpen ? onClose() : onOpen())}
        className="flex items-center gap-1.5 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] hover:opacity-80 underline-offset-4 hover:underline transition-all py-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {item.label}
        <ChevronDownIcon
          className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <div
        className={`absolute top-full left-0 pt-1 transition-all duration-200 ${
          isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1'
        }`}
      >
        <div className="min-w-[220px] bg-white rounded shadow-lg border border-[var(--vibe-color-border)] py-1 overflow-hidden">
          {item.children.map((child) => (
            <a
              key={child.href}
              href={child.href}
              className="block px-5 py-2.5 text-sm font-body text-black hover:bg-gray-100 hover:text-gray-900 hover:pl-6 transition-all duration-150"
            >
              {child.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileNavItem({
  item,
  isOpen,
  onToggle,
  onNavigate,
}: {
  item: NavItem;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  if (!item.children) {
    return (
      <a
        href={item.href}
        onClick={onNavigate}
        className="block py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[var(--vibe-color-text)] hover:opacity-80 transition-opacity border-b border-[var(--vibe-color-border)]/50"
      >
        {item.label}
      </a>
    );
  }

  return (
    <div className="border-b border-[var(--vibe-color-border)]/50">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[var(--vibe-color-text)] hover:opacity-80 transition-opacity"
        aria-expanded={isOpen}
      >
        {item.label}
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-gray-100 py-1">
          {item.children.map((child) => (
            <a
              key={child.href}
              href={child.href}
              onClick={onNavigate}
              className="block py-3 px-10 font-body text-sm text-gray-900 hover:bg-gray-200 hover:text-gray-900 transition-colors"
            >
              {child.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [openMobileAccordion, setOpenMobileAccordion] = useState<number | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  // Check whether the property has any virtual tours so we can hide the nav link
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Only communities with a map configured get the Interactive Map link
  const hasInteractiveMap = useHasInteractiveMap();

  // Derive nav items: filter out /virtual-tour when no virtual tours are configured
  // and /interactive-map when this community has no map, then collapse any dropdown
  // left with a single child into a flat nav link.
  const navItems = useMemo(
    () =>
      NAV_ITEMS.map((item) => {
        if (!item.children) return item;
        const children = item.children.filter((child) => {
          if (child.href === '/virtual-tour') return hasVirtualTours;
          if (child.href === '/interactive-map') return hasInteractiveMap;
          return true;
        });
        // Single child left — promote it to a flat link
        if (children.length === 1) {
          return { label: children[0].label, href: children[0].href };
        }
        return { ...item, children };
      }).filter((item) => !item.children || item.children.length > 0),
    [hasVirtualTours, hasInteractiveMap],
  );

  const phone = useContentToken('header.phone');
  const phoneForTel = cleanPhoneForTel(phone);
  const fullAddress = useContentToken('header.full_address');

  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');

  const logoImage = (logoRaw.image && monoWhiteLogo && logoRaw.image === monoWhiteLogo && monoBlackLogo)
    ? monoBlackLogo
    : logoRaw.image;
  const logo = { ...logoRaw, image: logoImage };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const payRentLink = useSlotGroup('header.pay_rent_link', payRentLinkDef);
  const payRentPropertyUrl = usePropertyField('custom_pay_rent_url');

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Directions URL resolves through usePropertyAddressUrl(), which falls back
  // through property_google_place_url -> property_google_business_url ->
  // property_full_address -> lat/lon. Final '#' guards against an empty value.
  const propertyAddressUrl = usePropertyAddressUrl();
  const directionsUrl = propertyAddressUrl || '#';

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setOpenMobileAccordion(null);
  };

  return (
    <>
      <div style={{ height: `calc(${headerHeight}px + var(--offer-banner-height, 0px))`, transition: 'height 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }} />

      <header ref={headerRef} className="fixed left-0 right-0 z-50 w-full" style={{ top: 'var(--offer-banner-height, 0px)', transition: 'top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}>
        {/* Utility Bar */}
        <div className="header-utility-bar text-[var(--vibe-color-text-on-secondary)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-9">
              <div className="hidden md:flex items-center divide-x divide-white/20">
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 pr-4 text-xs tracking-wide hover:text-[var(--utility-bar-hover)] transition-colors"
                >
                  <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <EditableToken
                    path="header.full_address"
                    defaultField="property_full_address"
                    defaultValue="123 Main Street, Atlanta, GA 30313"
                    as="span"
                  >
                    {fullAddress}
                  </EditableToken>
                </a>

                <a
                  href={phoneForTel ? `tel:${phoneForTel}` : '#'}
                  className="flex items-center gap-1.5 pl-4 text-xs tracking-wide hover:text-[var(--utility-bar-hover)] transition-colors"
                >
                  <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <EditableToken
                    path="header.phone"
                    defaultField="property_phone"
                    defaultValue="(555) 555-5555"
                    as="span"
                  >
                    {formatPhone(phone)}
                  </EditableToken>
                </a>
              </div>

              <div className="hidden md:flex items-center divide-x divide-white/20 ml-auto">
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase hover:text-[var(--utility-bar-hover)] transition-colors"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase hover:text-[var(--utility-bar-hover)] transition-colors"
                  >
                    {residentAccessLink.label || 'Resident Access'}
                  </a>
                </EditableSlotGroup>

                {payRentPropertyUrl && (
                  <EditableSlotGroup groupPath="header.pay_rent_link" definition={payRentLinkDef}>
                    <a
                      href={payRentLink.url || '#'}
                      target={payRentLink._target || '_self'}
                      className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase hover:text-[var(--utility-bar-hover)] transition-colors"
                    >
                      {payRentLink.label || 'Pay Rent'}
                    </a>
                  </EditableSlotGroup>
                )}
              </div>

              <div className="md:hidden flex items-center justify-between w-full text-xs">
                <a
                  href={phoneForTel ? `tel:${phoneForTel}` : '#'}
                  className="flex items-center gap-1.5 hover:text-[var(--utility-bar-hover)] transition-colors"
                >
                  <PhoneIcon className="w-3.5 h-3.5" />
                  <span>{formatPhone(phone)}</span>
                </a>
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 hover:text-[var(--utility-bar-hover)] transition-colors"
                >
                  <MapPinIcon className="w-3.5 h-3.5" />
                  <span>Get Directions</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Navigation Bar */}
        <div className="bg-white border-b border-[var(--vibe-color-border)] shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 lg:h-[72px]">
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" className="flex-shrink-0">
                  {logo.image ? (
                    <img
                      src={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="h-10 lg:h-12 w-auto object-contain"
                    />
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      The Avenue
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              <nav className="hidden lg:flex items-center gap-5 xl:gap-7" role="navigation" aria-label="Main navigation">
                {navItems.map((item, index) => (
                  <NavDropdown
                    key={item.label}
                    item={item}
                    isOpen={openDropdown === index}
                    onOpen={() => setOpenDropdown(index)}
                    onClose={() => setOpenDropdown(null)}
                  />
                ))}
              </nav>

              <div className="hidden lg:block flex-shrink-0">
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    className="nav-cta-btn group/cta relative inline-flex items-center gap-2 px-7 py-3.5 text-[11px] font-bold tracking-[0.15em] uppercase text-primary-foreground overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">
                      {scheduleTourLink.label || 'Schedule a Tour'}
                    </span>
                    <svg className="relative z-10 w-3.5 h-3.5 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </a>
                </EditableSlotGroup>
              </div>

              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden relative w-10 h-10 flex items-center justify-center"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`absolute block w-6 h-0.5 bg-black transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-2'
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-black transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-black transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-2'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div
          className={`lg:hidden fixed inset-x-0 bottom-0 bg-white overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{ top: `calc(${headerHeight}px + var(--offer-banner-height, 0px))` }}
        >
          <nav role="navigation" aria-label="Mobile navigation">
            {navItems.map((item, index) => (
              <MobileNavItem
                key={item.label}
                item={item}
                isOpen={openMobileAccordion === index}
                onToggle={() =>
                  setOpenMobileAccordion(openMobileAccordion === index ? null : index)
                }
                onNavigate={closeMobileMenu}
              />
            ))}
          </nav>

          <div className="p-5">
            <a
              href={scheduleTourLink.url || '/schedule-a-tour'}
              target={scheduleTourLink._target || '_self'}
              onClick={closeMobileMenu}
              className="nav-cta-btn group/cta relative flex items-center justify-center gap-2 w-full py-3.5 text-sm font-bold tracking-[0.15em] uppercase text-primary-foreground overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{scheduleTourLink.label || 'Schedule a Tour'}</span>
              <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </div>

          <div className="px-5 pb-3">
            <div className="flex gap-2">
              <a
                href={applyNowLink.url || '/apply-now'}
                target={applyNowLink._target || '_self'}
                className="flex-1 text-center py-2.5 text-[11px] font-bold tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] hover:text-white transition-all"
              >
                {applyNowLink.label || 'Apply Now'}
              </a>
              <a
                href={residentAccessLink.url || '/resident-access'}
                target={residentAccessLink._target || '_self'}
                onClick={closeMobileMenu}
                className="flex-1 text-center py-2.5 text-[11px] font-bold tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] hover:text-white transition-all"
              >
                {residentAccessLink.label || 'Resident Access'}
              </a>
              {payRentPropertyUrl && (
                <a
                  href={payRentLink.url || '#'}
                  target={payRentLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="flex-1 text-center py-2.5 text-[11px] font-bold tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] hover:text-white transition-all"
                >
                  {payRentLink.label || 'Pay Rent'}
                </a>
              )}
            </div>
          </div>

          <div className="px-5 pb-6 pt-3 border-t border-[var(--vibe-color-border)]/50">
            <div className="space-y-3">
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
              >
                <MapPinIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                <span>{fullAddress}</span>
              </a>
              <a
                href={phoneForTel ? `tel:${phoneForTel}` : '#'}
                className="flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
              >
                <PhoneIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                <span>{formatPhone(phone)}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      <style>{`
        .header-utility-bar {
          background: linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 85%, black) 100%);
          /* Hover colour always readable on the secondary background */
          --utility-bar-hover: color-mix(in srgb, var(--vibe-color-text-on-secondary) 70%, transparent);
        }
        .nav-cta-btn {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 1px 3px rgba(0,0,0,0.12),
            0 2px 8px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .nav-cta-btn:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 35%, transparent);
          transform: translateY(-1px);
        }
        .nav-cta-btn:hover::before {
          left: 100%;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.full_address': {
      label: 'Full Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultField: 'property_apply_now_url', suggestedFields: ['property_apply_now_url', 'property_website_url'] },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.pay_rent_link': {
      label: 'Pay Rent Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultField: 'custom_pay_rent_url' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Pay Rent' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
